import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Slider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import apiService from '../services/apiService';
import { geocodingService } from '../services/geocodingService';
import { locationService } from '../services/locationService';
import AddressSearchDialog from '../components/AddressSearchDialog';
import ListTileCard from '../components/ListTileCard';
import useListsStore from '../store/listsStore';
import weatherService from '../services/weatherService';

const DEFAULT_MAX_RADIUS_MI = 50;
const DEFAULT_RADIUS_MI = 10;

const STORAGE_KEYS = {
  radius: 'location_lists_radius_mi',
  location: 'location_lists_location',
};

function haversineDistanceMi(a, b) {
  if (!a || !b) return Infinity;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 3958.8; // Earth radius in miles
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const aVal = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
  return R * c;
}

const LocationListsPage = () => {
  const {
    lists,
    setLists,
    updateTaskInList,
    completeAllTasksInList,
    incompleteAllTasksInList,
    removeList,
    isListSelected,
    toggleListSelection,
    actionLoading,
    setActionLoading,
    clearActionLoading,
  } = useListsStore();

  const [allLists, setAllLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [address, setAddress] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [hoveredList, setHoveredList] = useState(null);

  const [radiusMi, setRadiusMi] = useState(DEFAULT_RADIUS_MI);
  const [maxRadiusMi] = useState(DEFAULT_MAX_RADIUS_MI);
  const [searchLocation, setSearchLocation] = useState(null); // { lat, lng }

  // Share dialog state (mirrors Dashboard)
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogList, setShareDialogList] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  // Delete dialog state (mirrors Dashboard)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);

  // Weather
  const [weatherData, setWeatherData] = useState({});

  // Load persisted settings
  useEffect(() => {
    try {
      const storedRadius = parseInt(localStorage.getItem(STORAGE_KEYS.radius), 10);
      if (!Number.isNaN(storedRadius)) {
        setRadiusMi(storedRadius);
      }
      const locStr = localStorage.getItem(STORAGE_KEYS.location);
      if (locStr) {
        const parsed = JSON.parse(locStr);
        if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') {
          setSearchLocation(parsed);
        }
      }
    } catch {}
  }, []);

  // Persist settings when changed
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.radius, String(radiusMi));
    } catch {}
  }, [radiusMi]);

  useEffect(() => {
    try {
      if (searchLocation) {
        localStorage.setItem(STORAGE_KEYS.location, JSON.stringify(searchLocation));
      } else {
        localStorage.removeItem(STORAGE_KEYS.location);
      }
    } catch {}
  }, [searchLocation]);

  // Load lists
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiService.getLists();
        const fetched = Array.isArray(resp?.lists) ? resp.lists : [];
        setAllLists(fetched);
        setLists(fetched);
      } catch (e) {
        console.error('Failed to load lists:', e);
        setError('Failed to load lists. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load weather similar to Dashboard
  useEffect(() => {
    const loadWeather = async (listsData) => {
      const listsWithLocation = (listsData || []).filter(list =>
        list.location_data && list.location_data.coordinates && list.location_data.coordinates.lat && list.location_data.coordinates.lng
      );
      if (listsWithLocation.length === 0) return;
      try {
        const locations = listsWithLocation.map(list => ({ id: String(list.id), latitude: list.location_data.coordinates.lat, longitude: list.location_data.coordinates.lng }));
        const results = await weatherService.getWeatherForLocations(locations);
        const map = {};
        results.forEach(w => { if (w.id) map[w.id] = w; });
        setWeatherData(map);
      } catch (e) {
        console.warn('Failed to load weather data:', e);
      }
    };
    if ((lists || []).length > 0) {
      loadWeather(lists);
    }
  }, [lists]);

  // Resolve address for current search location
  useEffect(() => {
    const resolveAddress = async () => {
      if (!searchLocation) {
        setAddress('');
        return;
      }
      setAddressLoading(true);
      try {
        const addr = await geocodingService.getAddressFromCoordinates(
          searchLocation.lat,
          searchLocation.lng,
          apiService.getGoogleMapsApiKey()
        );
        setAddress(addr);
      } catch (e) {
        setAddress(`${searchLocation.lat.toFixed(6)}, ${searchLocation.lng.toFixed(6)}`);
      } finally {
        setAddressLoading(false);
      }
    };
    resolveAddress();
  }, [searchLocation]);

  const filteredLists = useMemo(() => {
    const source = lists || [];
    if (!searchLocation) return source;
    return source.filter((list) => {
      const coords = list?.location_data?.coordinates;
      if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') return false;
      const distance = haversineDistanceMi(searchLocation, { lat: coords.lat, lng: coords.lng });
      return distance <= radiusMi;
    });
  }, [lists, searchLocation, radiusMi]);

  const handleUseCurrentLocation = async () => {
    try {
      const location = await locationService.getCurrentLocation({ enableHighAccuracy: true, timeout: 10000 });
      if (location?.coordinates) {
        setSearchLocation({ lat: location.coordinates.lat, lng: location.coordinates.lng });
      }
    } catch (err) {
      console.warn('Failed to get current location:', err);
      setError(locationService.getErrorMessage(err));
    }
  };

  const handleOpenSearch = useCallback(() => setSearchDialogOpen(true), []);
  const handleCloseSearch = useCallback(() => setSearchDialogOpen(false), []);

  const handleLocationFromSearch = (result) => {
    try {
      const coords = result?.coordinates;
      if (coords) {
        setSearchLocation({ lat: coords.lat, lng: coords.lng });
        if (result.address) setAddress(result.address);
      }
    } catch (e) {
      console.error('Failed to set location from search:', e);
    } finally {
      setSearchDialogOpen(false);
    }
  };

  const handleClearLocation = () => {
    setSearchLocation(null);
    setAddress('');
  };

  // Tile action handlers (mirror Dashboard)
  const handleTaskToggle = async (listId, taskId, currentCompleted) => {
    try {
      updateTaskInList(listId, taskId, { completed: !currentCompleted });
      await apiService.updateTask(listId, taskId, { completed: !currentCompleted });
    } catch (err) {
      console.error('Failed to toggle task:', err);
      updateTaskInList(listId, taskId, { completed: currentCompleted });
      setError('Failed to update task. Please try again.');
    }
  };

  const handleCompleteAll = async (listId) => {
    setActionLoading(`complete-${listId}`, true);
    try {
      completeAllTasksInList(listId);
      await apiService.completeAllTasks(listId);
    } catch (err) {
      console.error('Failed to complete all tasks:', err);
      setError('Failed to complete all tasks. Please try again.');
    } finally {
      setActionLoading(`complete-${listId}`, false);
    }
  };

  const handleIncompleteAll = async (listId) => {
    setActionLoading(`incomplete-${listId}`, true);
    try {
      incompleteAllTasksInList(listId);
      await apiService.incompleteAllTasks(listId);
    } catch (err) {
      console.error('Failed to incomplete all tasks:', err);
      setError('Failed to incomplete all tasks. Please try again.');
    } finally {
      setActionLoading(`incomplete-${listId}`, false);
    }
  };

  const handleDeleteList = (listId) => {
    const list = (lists || []).find(l => l.id === listId);
    setListToDelete(list);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!listToDelete) return;
    const listId = listToDelete.id;
    setActionLoading(`delete-${listId}`, true);
    setDeleteDialogOpen(false);
    try {
      removeList(listId);
      await apiService.deleteList(listId);
    } catch (err) {
      console.error('Failed to delete list:', err);
      setError('Failed to delete list. Please try again.');
    } finally {
      setActionLoading(`delete-${listId}`, false);
      setListToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setListToDelete(null);
  };

  const handleShareList = (event, listId) => {
    event.stopPropagation();
    const list = (lists || []).find(l => l.id === listId);
    setShareDialogList(list);
    setShareDialogOpen(true);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setShareDialogList(null);
    setShareEmail('');
    setShareError(null);
    setShareSuccess(false);
    setShareLoading(false);
  };

  const handleSendShareEmail = async () => {
    if (!shareEmail.trim() || !shareDialogList) return;
    setShareLoading(true);
    setShareError(null);
    try {
      await apiService.shareListByEmail(shareDialogList.id, shareEmail.trim());
      setShareSuccess(true);
      setTimeout(() => { handleCloseShareDialog(); }, 2000);
    } catch (error) {
      console.error('Failed to share list:', error);
      setShareError('Failed to send email. Please try again.');
    } finally {
      setShareLoading(false);
    }
  };

  const handleExportList = async (event, listId) => {
    event.stopPropagation();
    setActionLoading(`export-${listId}`, true);
    try {
      await apiService.exportListAsJSON(listId);
    } catch (error) {
      console.error('Failed to export list:', error);
      setError('Failed to export list. Please try again.');
    } finally {
      setActionLoading(`export-${listId}`, false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1">
            Location Lists
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Location controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="outlined" size="small" onClick={handleUseCurrentLocation} disabled={addressLoading}>
            {addressLoading ? 'Getting Location…' : (searchLocation ? 'Update to Current Location' : 'Add Current Location')}
          </Button>
          <Button variant="outlined" size="small" onClick={handleOpenSearch} disabled={addressLoading}>
            Search Address
          </Button>
          {(searchLocation) && (
            <Button variant="outlined" size="small" color="error" onClick={handleClearLocation} disabled={addressLoading}>
              Clear Location
            </Button>
          )}
          {(searchLocation || addressLoading) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color={addressLoading ? 'text.secondary' : 'text.primary'}>
                {addressLoading ? 'Looking up address…' : (address || `${searchLocation?.lat?.toFixed?.(6)}, ${searchLocation?.lng?.toFixed?.(6)}`)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Radius slider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
            Radius: {radiusMi} mi
          </Typography>
          <Slider
            value={radiusMi}
            min={1}
            max={maxRadiusMi}
            step={1}
            valueLabelDisplay="auto"
            onChange={(_, val) => setRadiusMi(val)}
            sx={{ maxWidth: 320 }}
          />
          <Typography variant="body2" color="text.secondary">(Max {maxRadiusMi} mi)</Typography>
        </Box>
      </Box>

      {/* Lists */}
      {(filteredLists || []).length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No lists match the current location/radius.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredLists.map((list) => (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={list.id}
              onMouseEnter={() => setHoveredList(list.id)}
              onMouseLeave={() => setHoveredList(null)}
            >
              <ListTileCard
                list={list}
                isHovered={hoveredList === list.id}
                onMouseEnter={() => setHoveredList(list.id)}
                onMouseLeave={() => setHoveredList(null)}
                isSelected={isListSelected(list.id)}
                onToggleSelection={() => toggleListSelection(list.id)}
                weather={weatherData[list.id]}
                actionLoading={actionLoading}
                onCompleteAll={handleCompleteAll}
                onIncompleteAll={handleIncompleteAll}
                onDeleteList={handleDeleteList}
                onShareList={handleShareList}
                onExportList={handleExportList}
                onTaskToggle={handleTaskToggle}
                // Omit onEditList to keep editing on Dashboard for now
                googleMapsApiKey={apiService.getGoogleMapsApiKey()}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <AddressSearchDialog open={searchDialogOpen} onClose={handleCloseSearch} onLocationSelect={handleLocationFromSearch} />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete List</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete "{listToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={!!(listToDelete && actionLoading[`delete-${listToDelete.id}`])}>
            {listToDelete && actionLoading[`delete-${listToDelete.id}`] ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share List Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Share List via Email</DialogTitle>
        <DialogContent>
          {shareSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main" gutterBottom>✅ Email Sent Successfully!</Typography>
              <Typography variant="body2" color="text.secondary">"{shareDialogList?.title}" has been shared with {shareEmail}</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>Share "{shareDialogList?.title}" with someone via email.</Typography>
              <TextField autoFocus margin="dense" label="Email Address" type="email" fullWidth variant="outlined" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} onKeyPress={(e) => { if (e.key === 'Enter') handleSendShareEmail(); }} placeholder="Enter recipient's email address" error={!!shareError} helperText={shareError} disabled={shareLoading} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The email will include the list title, all tasks with completion status{shareDialogList?.location_data && ', and a map showing the location'}.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!shareSuccess && (
            <>
              <Button onClick={handleCloseShareDialog} disabled={shareLoading}>Cancel</Button>
              <Button onClick={handleSendShareEmail} variant="contained" disabled={!shareEmail.trim() || shareLoading}>
                {shareLoading ? <CircularProgress size={20} /> : 'Send Email'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LocationListsPage;
